import {
  SelectOptionsHook,
  Body2,
  Caption,
  SingleSelect,
  authorizeBaseUrl,
  caseTypeLabel,
  getCaseRedirectUrl,
  useFeature,
  dateStringSort,
} from "@coherehealth/common";
import {
  RouteConfigByCaseType,
  ServiceCase,
  ServiceRequestStatus,
  useServiceCaseSimpleSearch,
} from "@coherehealth/qm-api";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import SelectAServiceRequestModal from "./modals/SelectAServiceRequest";
import debounce from "lodash/debounce";
import { getRedirectUrlServiceCaseDetailsFaxIntake } from "util/serviceCase";

const isCaseNumberSearch = (query?: string, option?: ServiceCase) => {
  return !!query && !!option?.caseNumber?.includes(query.toUpperCase());
};

interface SupervisorSearchProps {
  routeConfigByCaseType?: RouteConfigByCaseType;
  activeQueueId?: string;
}

export default function SupervisorSearch({ routeConfigByCaseType, activeQueueId }: SupervisorSearchProps) {
  const isNewFaxOutcomePageEnabled = useFeature("enableNewFaxOutcomePage");

  const [searchText, setSearchText] = useState<string>();
  const [selectedValue, setSelectedValue] = useState<ServiceCase>();
  const [selectAServiceRequestDialogOpen, setSelectAServiceRequestDialogOpen] = useState<boolean>(false);

  const renderOption = (option: ServiceCase) => {
    if (isCaseNumberSearch(searchText, option)) {
      return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Grid container sx={{ width: 345 }}>
            <Grid item xs={12}>
              <OptionBody2>
                {option.caseType && caseTypeLabel(option.caseType) + ": "} {option.caseNumber}
              </OptionBody2>
            </Grid>
            {option?.outcome?.serviceRequests?.find((request: ServiceRequestStatus) => !!request?.cohereId) && (
              <Grid item xs={12}>
                <OptionCaption>
                  Tracking: #
                  {option?.outcome?.serviceRequests
                    ?.map((request: ServiceRequestStatus) => request.cohereId)
                    .join(", ")}
                </OptionCaption>
              </Grid>
            )}
          </Grid>
        </div>
      );
    } else {
      const optionTrackingNumber =
        option.caseType === "FAX_INTAKE"
          ? option?.outcome?.serviceRequests?.find((request: ServiceRequestStatus) =>
              request?.cohereId?.includes(searchText?.toUpperCase() || "")
            )?.cohereId
          : option.serviceRequest?.cohereId;

      return (
        <Grid container>
          <Grid item xs={12}>
            <OptionBody2>
              {option.caseType && caseTypeLabel(option.caseType) + ": "} #{optionTrackingNumber}
            </OptionBody2>
          </Grid>
          <Grid item xs={12}>
            <OptionCaption>{option.caseNumber}</OptionCaption>
          </Grid>
        </Grid>
      );
    }
  };

  const onDialogClose = () => {
    setSelectAServiceRequestDialogOpen(false);
    setSelectedValue(undefined);
  };

  const handleSelection = useCallback(
    (serviceCase: ServiceCase | null) => {
      if (!serviceCase) {
        setSearchText("");
        return;
      }

      setSelectedValue(serviceCase || undefined);

      const caseUrl = getCaseRedirectUrl({
        caseId: serviceCase.id,
        caseType: serviceCase.caseType,
        serviceRequestId: serviceCase.serviceRequest?.serviceRequestId,
        faxId: serviceCase?.faxDetail?.attachmentId,
        isPostDenialP2P: serviceCase.isPostDenialP2P,
        fromSupervisorDashboard: true,
        routeConfigByCaseType,
        queueId: activeQueueId,
      });

      if (serviceCase && serviceCase.caseType !== "FAX_INTAKE") {
        caseUrl && window.location.assign(caseUrl);
      } else if (isCaseNumberSearch(searchText, serviceCase || null)) {
        if (serviceCase?.caseStatus === "COMPLETE") {
          if (serviceCase?.outcome?.serviceRequests && serviceCase?.outcome?.serviceRequests.length > 1) {
            setSelectAServiceRequestDialogOpen(true);
          } else {
            const redirectUrl = getRedirectUrlServiceCaseDetailsFaxIntake(serviceCase, isNewFaxOutcomePageEnabled);
            window.location.assign(redirectUrl);
          }
        } else {
          caseUrl && window.location.assign(caseUrl);
        }
      } else {
        window.location.assign(
          `${authorizeBaseUrl()}/view_only_review?caseId=${serviceCase?.id}&serviceRequestId=${
            serviceCase?.outcome?.serviceRequests?.find((request: ServiceRequestStatus) =>
              request.cohereId?.includes(searchText?.toUpperCase() || "")
            )?.serviceRequestId
          }&trackingNumberSearch=${true}`
        );
      }
    },
    [activeQueueId, isNewFaxOutcomePageEnabled, routeConfigByCaseType, searchText]
  );

  return (
    <>
      <div style={{ width: 400 }}>
        <SingleSelect
          autoHighlight
          useOptions={useSimpleSearchOptions}
          label="Search Case ID or Tracking #"
          renderOption={(option: ServiceCase, state) => renderOption(option)}
          setSelectedValue={(option: ServiceCase | null) => handleSelection(option)}
          getOptionLabel={(option: ServiceCase) =>
            isCaseNumberSearch(searchText, option) ? option.caseNumber || "" : searchText || ""
          }
          selectedValue={selectedValue || null}
          fullWidth
          markSelectedOptions={false}
          inputValue={searchText || ""}
          onInputChange={(_, value) => setSearchText(value)}
          clearable
        />
      </div>
      <SelectAServiceRequestModal
        dialogOpen={selectAServiceRequestDialogOpen}
        onClose={onDialogClose}
        serviceRequestIds={selectedValue?.outcome?.serviceRequests?.map((sr) => sr.serviceRequestId || "")}
        serviceCaseId={selectedValue?.id || ""}
      />
    </>
  );
}

const useSimpleSearchOptions: SelectOptionsHook<ServiceCase> = ({ query }) => {
  const {
    data,
    loading,
    refetch: search,
  } = useServiceCaseSimpleSearch({
    queryParams: {
      query: query?.replace("#", "") || "",
    },
    lazy: true,
  });
  const debouncedSearch = useMemo(() => debounce(search, 500), [search]);

  useEffect(() => {
    let ignore = false;
    if (query && query?.length >= 8 && !ignore) {
      debouncedSearch();
    }
    return () => {
      debouncedSearch.cancel();
      ignore = true;
    };
  }, [debouncedSearch, query]);

  const sortedOptions = data?.slice().sort((a, b) => dateStringSort(a.lastUpdated || "", b.lastUpdated || "", false));

  return {
    optionsLoading: loading,
    options: sortedOptions,
  };
};

const OptionBody2 = styled(Body2)({
  marginLeft: -5,
  marginTop: -5,
});

const OptionCaption = styled(Caption)({
  marginLeft: -5,
  display: "block",
});
